import React, { useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";

import Drawer from "@mui/material/Drawer";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import FormButtons from "views/components/formbuttons";
import FormHeader from "views/components/formheader";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";

const AddUser = (props) => {
  const { open, toggle, type, heading, updateData, onSubmitUser } = props;

  const defaultValues = {
    name: "",
    membership: "",
    phone: "",
    email: "",
  };

  const {
    setValue,
    reset,
    control,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues });

  const resetToEmptyValues = useCallback(() => {
    setValue("name", "");
    setValue("phone", "");
    setValue("email", "");
    setValue("membership", "");
  }, [setValue]);

  const resetToStoredValues = useCallback(() => {
    if (type === "edit") {
      const event = {
        name: updateData?.first_name,
        phone: `${updateData?.country_code}-${updateData?.phone_no}`,
        email: updateData?.email,
        membership: updateData?.member_type,
      };
      setValue("name", event.name || "");
      setValue("phone", event.phone || "");
      setValue("email", event.email || "");
      setValue("membership", event.membership);
    }
  }, [
    setValue,
    type,
    updateData?.country_code,
    updateData?.email,
    updateData?.member_type,
    updateData?.first_name,
    updateData?.phone_no,
  ]);

  useEffect(() => {
    if (type === "edit") {
      resetToStoredValues();
    } else {
      resetToEmptyValues();
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type]);

  const handleClose = () => {
    clearErrors();
    reset();
    toggle();
  };

  const onSubmit = (data) => {
    const payload = {
      user_id: updateData.user_id,
      status: data.membership,
    };
    APICall("post", payload, EndPoints.changeUserMemberType)
      .then((response) => {
        const { message, isError } = response.data;
        if (response.status === 200 && !isError) {
          toast.success(message);
          onSubmitUser();
          handleClose();
        }
      })
      .catch((error) => {
        return error;
      });
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 300, sm: 400 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Name"
                      disabled={updateData}
                      placeholder="Enter name"
                      error={Boolean(errors.name)}
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.name && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      type="email"
                      value={value}
                      label="Email"
                      disabled={updateData}
                      onChange={onChange}
                      error={Boolean(errors.email)}
                      placeholder="carterleonard@gmail.com"
                      aria-describedby="validation-basic-email"
                    />
                  )}
                />
                {errors.email && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-email"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="phone"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      disabled={updateData}
                      label="Phone"
                      placeholder="+1-123-456-8790"
                      value={value}
                      onChange={onChange}
                      error={Boolean(errors.phone)}
                      aria-describedby="validation-basic-phone"
                    />
                  )}
                />
                {errors.phone && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-phone"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel
                  id="validation-basic-select"
                  error={Boolean(errors.membership)}
                  htmlFor="validation-basic-select"
                >
                  Member Type
                </InputLabel>
                <Controller
                  name="membership"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      value={value}
                      label="Membership"
                      onChange={onChange}
                      error={Boolean(errors.membership)}
                      labelId="validation-basic-select"
                      aria-describedby="validation-basic-select"
                    >
                      <MenuItem value={0}>Non Member</MenuItem>
                      <MenuItem value={1}>Member</MenuItem>
                    </Select>
                  )}
                />
                {errors.membership && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddUser;
